import type { CanvasTab } from '../canvas/CanvasContext';
import { RECYCLE_BIN_APP_PATH } from './recycleBinChatNavigation';

/**
 * Kept free of component and store imports so it stays testable — pulling in
 * the chat components drags the whole Redux store in with them.
 */

export type SourceHit = {
  iconKey: string;
  label: string;
  to?: string;
  trackId?: string;
  canvas?: CanvasTab;
  fromPage?: string;
};

/**
 * Canvases the agent is allowed to open by name, each with the route to fall
 * back to when the link is followed from somewhere without a canvas host.
 */
const CANVAS_LINKS: Record<string, { tab: CanvasTab; route: string }> = {
  duplicates: {
    tab: { id: 'duplicates', kind: 'duplicates', title: 'Duplicates' },
    route: '/resolve-duplicates',
  },
};

export const parseSourceHref = (href: string): SourceHit | null => {
  if (!href) return null;
  const trimmed = href.trim();

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return {
      iconKey: 'flameLogo',
      label: 'Crate Hackers',
      to: trimmed,
    };
  }

  const match = trimmed.match(
    /^(ch|serato|vdj|spotify|tidal|apple):(?:\/\/)?([^/?#]+)(?:\/([^?#]*))?/i,
  );
  if (!match) return null;

  const protocol = match[1].toLowerCase();
  const kind = match[2].toLowerCase();
  const id = decodeURIComponent(match[3] || '')
    .replace(/\/$/, '')
    .trim();

  if (protocol === 'ch') {
    if (kind === 'canvas') {
      const canvas = CANVAS_LINKS[id];
      if (!canvas) return null;
      return {
        iconKey: 'flameLogo',
        label: `Open ${canvas.tab.title} beside the chat`,
        to: canvas.route,
        canvas: canvas.tab,
      };
    }
    if (kind === 'track') {
      return {
        iconKey: 'flameLogo',
        label: 'Crate Hackers track',
        to: id ? '/track' : undefined,
        trackId: id || undefined,
      };
    }
    if (kind === 'mine') {
      return {
        iconKey: 'flameLogo',
        label: 'Your Crate Hackers crate',
        to: id ? `/crate/${id}` : '/my-crates',
        fromPage: id ? 'my-crates' : undefined,
      };
    }
    if (kind === 'example') {
      return {
        iconKey: 'flameLogo',
        label: 'Crate Hackers example crate',
        to: id ? `/crate/${id}` : '/crates',
      };
    }
    if (kind === 'crate') {
      return {
        iconKey: 'flameLogo',
        label: 'Crate Hackers crate',
        to: id ? `/crate/${id}` : '/crates',
      };
    }
    if (kind === 'recycle-bin') {
      return {
        iconKey: 'flameLogo',
        label: 'Recycle bin',
        to: RECYCLE_BIN_APP_PATH,
      };
    }
  }

  if (protocol === 'serato') {
    return {
      iconKey: 'seratoDJPro',
      label: 'Serato crate',
      to: '/serato-crates',
    };
  }
  if (protocol === 'vdj') {
    return { iconKey: 'virtualDJ', label: 'VirtualDJ playlist' };
  }
  if (protocol === 'spotify') {
    return { iconKey: 'spotify', label: 'Spotify playlist' };
  }
  if (protocol === 'tidal') {
    return { iconKey: 'tidal', label: 'Tidal playlist' };
  }
  if (protocol === 'apple') {
    return { iconKey: 'apple', label: 'Apple Music playlist' };
  }

  return null;
};
